import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Button,
  SplitButton,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Tooltip,
} from '@fluentui/react-components';
import { useStyles } from './style';

export const SaveButtonInSummaryBar = ({
  i18n,
  onSaveDirectly,
  onSaveAsCopy,
  isSaveAsCopyDisabled,
  isSaveDisabled,
  haveCroppingTasks,
  isBaseButton,
  baseButtonText,
  onBaseButtonClick,
  saveButtonTooltip,
}) => {
  const classes = useStyles();

  const [summaryBar, setSummaryBar] = useState(null);
  const [open, setOpen] = useState(false);

  const onOpenChange = useCallback((e, data) => {
    setOpen(data.open);
  }, []);

  const renderBaseSaveButton = useCallback(() => (
    <Button
      appearance="primary"
      className={classes.baseSaveButton}
      disabled={isSaveDisabled}
      onClick={onBaseButtonClick}
    >
      {baseButtonText}
    </Button>
  ), [baseButtonText, classes, isSaveDisabled, onBaseButtonClick]);

  const baseSaveButtonWithTooltip = useMemo(() => (
    saveButtonTooltip ?
      <Tooltip
        withArrow
        relationship="label"
        positioning="below"
        content={saveButtonTooltip}
      >
        {renderBaseSaveButton()}
      </Tooltip> :
      renderBaseSaveButton()
  ), [renderBaseSaveButton, saveButtonTooltip]);

  const saveAsCopyMenuItem = useCallback(() => (
    <MenuItem
      onClick={onSaveAsCopy}
      disabled={isSaveAsCopyDisabled}
    >
      {i18n.getString(_TL_('Save as copy'))}
    </MenuItem>
  ), [i18n, isSaveAsCopyDisabled, onSaveAsCopy]);

  const splitSaveButton = useMemo(() => (
    <Menu open={open} onOpenChange={onOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        {triggerProps => (
          <SplitButton
            menuButton={triggerProps}
            appearance="primary"
            primaryActionButton={{
              onClick: onSaveDirectly,
            }}
            disabled={isSaveDisabled}
          >
            {i18n.getString(_TL_('Save'))}
          </SplitButton>
        )}
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          <MenuItem onClick={onSaveDirectly}>{i18n.getString(_TL_('Save'))}</MenuItem>
          {isSaveAsCopyDisabled ?
            <Tooltip
              withArrow
              relationship="label"
              positioning="below"
              content={i18n.getString(_TL_('You can\'t save as a copy since the image wasn\'t updated.'))}
            >
              {saveAsCopyMenuItem()}
            </Tooltip> :
            saveAsCopyMenuItem()
          }
        </MenuList>
      </MenuPopover>
    </Menu>
  ), [i18n, isSaveAsCopyDisabled, isSaveDisabled, onOpenChange, onSaveDirectly, open, saveAsCopyMenuItem]);

  const customizedContent = useMemo(() => (
    <div className={classes.container} style={{ marginRight: haveCroppingTasks ? 168 : 0 }}>
      {isBaseButton ? baseSaveButtonWithTooltip : splitSaveButton}
    </div>
  ), [classes.container, haveCroppingTasks, isBaseButton, baseSaveButtonWithTooltip, splitSaveButton]);

  useEffect(() => {
    const timer = setInterval(() => {
      try {
        const summaryBarCustomizeEle = document.getElementById('ad-studio-summary-bar-customize-container');
        if (summaryBarCustomizeEle) {
          setSummaryBar(summaryBarCustomizeEle);
          clearInterval(timer);
        }
      } catch (error) {
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    summaryBar ? createPortal(
      customizedContent,
      summaryBar
    ) : null
  );
};

SaveButtonInSummaryBar.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onSaveDirectly: PropTypes.func.isRequired,
  onSaveAsCopy: PropTypes.func.isRequired,
  isSaveAsCopyDisabled: PropTypes.bool,
  isSaveDisabled: PropTypes.bool,
  haveCroppingTasks: PropTypes.bool,
  isBaseButton: PropTypes.bool,
  baseButtonText: PropTypes.string,
  onBaseButtonClick: PropTypes.func,
  saveButtonTooltip: PropTypes.string,
};

SaveButtonInSummaryBar.defaultProps = {
  isSaveAsCopyDisabled: false,
  isSaveDisabled: false,
  haveCroppingTasks: false,
  isBaseButton: false,
  baseButtonText: undefined,
  onBaseButtonClick: _.noop,
  saveButtonTooltip: undefined,
};
